import * as React from "react"
import {
  Flex,
  Icon,
  IconButton,
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverAnchor,
  Portal,
  Progress,
  Stack,
  Text,
  useTheme,
  Tag as ChakraTag,
} from "@chakra-ui/react"

import { FiExternalLink } from "react-icons/fi"
import { Link as RLink } from "react-router-dom"

import { AlertBikeEntity, AlertEntity } from "../lib/types/alert"
import { LocationLink } from "./LocationLink"
import { BIKE_MARKER_COLORS, BIKE_MARKER_TEXT_COLORS } from "../lib/static/bike"
import { useOrgs } from "./providers/OrgProvider"
import { AlertItem } from "./AlertItem"
import { MdDirectionsBike } from "react-icons/md"
import { humanize } from "../lib/helpers/text"
import { useFeatFlags } from "./providers/FeatFlagProvider"

interface Props {
  bike: AlertBikeEntity
  onDeselect: () => void
  alerts: AlertEntity[]
  selectedUuid?: string
  setSelectedUuid: (uuid: string) => void
  refetchAlerts: () => Promise<any>
}

export const BikeAlertBikeMarker: React.FC<Props> = ({
  bike,
  selectedUuid,
  setSelectedUuid,
  onDeselect,
  alerts,
  refetchAlerts,
}) => {
  const openInNewTab = (url: string) => window.open(url, "_blank")
  const theme = useTheme()
  const { currentOrgs } = useOrgs()
  const { tags } = useFeatFlags()

  const [open, setOpen] = React.useState(false)
  const toRef = React.useRef<NodeJS.Timeout>()

  React.useEffect(() => {
    if (selectedUuid === bike.bicycle_uuid) {
      toRef.current = setTimeout(() => {
        setOpen(true)
      }, 50)
    } else {
      setOpen(false)
      if (toRef.current) {
        clearTimeout(toRef.current)
        toRef.current = undefined
      }
    }
    return () => {
      if (toRef.current) {
        clearTimeout(toRef.current)
        toRef.current = undefined
      }
    }
  }, [bike, selectedUuid])

  return (
    <Popover isOpen={open} onClose={onDeselect}>
      <PopoverAnchor>
        <IconButton
          aria-label="alert marker"
          boxSize="40px"
          borderRadius="50%"
          overflow="hidden"
          _hover={{
            bg: bike.bicycle_state ? theme.colors[BIKE_MARKER_COLORS[bike.bicycle_state]]["300"] : "blue.300",
          }}
          icon={<Icon as={MdDirectionsBike} boxSize="18px" color="white" />}
          bg={bike.bicycle_state ? theme.colors[BIKE_MARKER_COLORS[bike.bicycle_state]]["400"] : "blue.300"}
          onClick={() => setSelectedUuid(bike.bicycle_uuid)}
        />
      </PopoverAnchor>
      <Portal>
        <PopoverContent w="350px" zIndex={1000} bg="white">
          <PopoverHeader>
            <Flex align="center" justify="space-between">
              <Link as={RLink} to={`/assets/${bike.bicycle_uuid}`} fontSize="1.2rem">
                {bike.bicycle_name}
              </Link>
              <IconButton
                aria-label="go to asset"
                icon={<FiExternalLink />}
                boxSize="30px"
                cursor="pointer"
                minW="30px"
                p={2}
                variant="ghost"
                onClick={() => openInNewTab(`/assets/${bike.bicycle_uuid}`)}
              />
            </Flex>
          </PopoverHeader>
          <PopoverBody>
            <Stack spacing={2}>
              <Stack spacing={1} maxW={360}>
                <Text color="gray.700" fontSize="0.8rem">
                  Tracker ID: {bike.lock_bleid || "N/A"}
                </Text>
                <Text color="gray.700" fontSize="0.8rem">
                  Asset Location for this Alert
                </Text>
                {bike.location && bike.bicycle_coordinates && (
                  <LocationLink
                    obj={{
                      latitude: bike.bicycle_coordinates[1],
                      longitude: bike.bicycle_coordinates[0],
                    }}
                  />
                )}
                <Stack spacing={4} py={2}>
                  {alerts.map((alert) => {
                    const alertOrg = currentOrgs.find(({ url }) => url === alert.organization)!
                    return (
                      <AlertItem
                        key={alert.uuid}
                        alert={alert}
                        refetchAlerts={refetchAlerts}
                        organization={alertOrg}
                        onMuteAlert={() => null}
                      />
                    )
                  })}
                </Stack>
                <Stack spacing={1}>
                  <Text color="gray.800" fontSize="0.8rem">
                    State:{" "}
                    <Text as="span" color={BIKE_MARKER_TEXT_COLORS[bike.bicycle_state]}>
                      {humanize(bike.bicycle_state)}
                    </Text>
                  </Text>
                </Stack>
              </Stack>
            </Stack>
            {tags.visible &&
              bike.bicycle_tags?.map((tag) => {
                return (
                  <ChakraTag bg={tag.tag_declaration_color} key={tag.uuid} m="2">
                    {tag.tag_declaration_name}
                  </ChakraTag>
                )
              })}
            <Stack spacing={2}>
              <Stack spacing={1}>
                <Flex align="center" justify="space-between">
                  <Flex align="center">
                    <Text fontSize="0.7rem">{Math.ceil(bike.state_of_charge)}%</Text>
                    <Progress
                      size="sm"
                      ml={1}
                      h="5px"
                      borderRadius="md"
                      colorScheme={
                        bike.state_of_charge < 10 ? "red" : bike.state_of_charge < 50 ? "orange" : "green"
                      }
                      w="100px"
                      value={bike.state_of_charge}
                    />
                  </Flex>
                </Flex>
              </Stack>
            </Stack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}
